import React,{useCallback,useEffect,useRef,useState} from 'react';
import { Link } from 'react-router-dom';
import { FileCheck2,RefreshCw,LockKeyhole } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../i18n/Locale';
import { useGuardResource } from './useGuardResource';
import { guardIdentity } from './guardIdentity';
import { quoteCopy } from './quoteCopy';
import { validQuoteReadiness } from './quoteValidation';
import { useQuoteFocus } from './useQuoteFocus';
import { QuoteResearch } from './QuoteResearch';

export const UnsignedQuotes=({data})=>{
  const {language}=useLanguage(),t=quoteCopy[language]||quoteCopy.en;
  const [refresh,setRefresh]=useState(0),[now,setNow]=useState(Date.now());
  const identity=`${guardIdentity(data)}:${refresh}`,resource=useGuardResource(identity),lock=useRef(false);
  const localRefresh=useCallback(()=>setRefresh(n=>n+1),[]);
  useQuoteFocus(resource.invalidate,localRefresh);
  useEffect(()=>{resource.call('/executor/quote-readiness');},[identity,resource.call]);
  useEffect(()=>{const timer=setInterval(()=>setNow(Date.now()),1000);return()=>clearInterval(timer);},[]);
  const valid=validQuoteReadiness(resource.value,now),expired=valid&&resource.value.expires_at*1000<=now;
  const value=valid&&!expired?resource.value:null;
  const check=async()=>{if(lock.current)return;lock.current=true;try{await resource.call('/executor/check-quote-provider','post');}finally{lock.current=false;}};
  const rows=value?[
    ['key',t.key,value.provider_key_configured?t.configured:t.missing],['pilot',t.pilot,value.pilot_configured?t.configured:t.missing],
    ['wallet',t.wallet,value.wallet_verified?t.verified:t.pending],['budget',t.budget,`US$${(value.budget_daily_limit_cents/100).toFixed(2)} · ${t.states[value.budget_state]}`],
    ['guard',t.guard,value.guard_binding_recorded?t.recorded:t.missing],['version',t.version,t.versionValue],
    ['account',t.account,t.accountValue],['commitment',t.commitment,t.commitmentValue],['economic',t.economic,t.economicValue]
  ]:[];
  return <section className="backend-executor quote-readiness" data-testid="quote-readiness-panel">
    <div className="executor-title"><h2 data-testid="quote-readiness-title"><FileCheck2 size={17}/>{t.title}</h2><span data-testid="quote-readiness-phase">{t.phase}</span></div>
    {resource.busy&&<p role="status" data-testid="quote-readiness-loading">{t.loading}</p>}
    {(resource.error||(!valid&&resource.value))&&<p role="alert" data-testid="quote-readiness-error">{t.unavailable}</p>}
    {expired&&<p role="status" data-testid="quote-readiness-expired">{t.expired}</p>}
    <dl className="quote-readiness-list" data-testid="quote-readiness-facts">{rows.map(([id,label,text])=><div key={id}>
      <dt data-testid={`quote-readiness-${id}-label`}>{label}</dt><dd data-testid={`quote-readiness-${id}`}>{text}</dd>
    </div>)}</dl>
    {value?.provider&&<div className="quote-provider-observation" data-testid="quote-provider-observation">
      <p data-testid="quote-provider-scope">{t.observed}</p>
      <p data-testid="quote-provider-chain">{t.chain}: {value.provider.polygon_listed?t.listed:t.notListed}</p>
      <p data-testid="quote-provider-token">{t.token}: {value.provider.usdc_listed?t.listed:t.notListed}</p>
      <p data-testid="quote-provider-expiry">{t.until}: {new Date(value.expires_at*1000).toLocaleTimeString(language==='es'?'es':'en')}</p>
      <p className="pilot-muted" data-testid="quote-provider-notice">{t.notice}</p>
    </div>}
    <div className="executor-actions">
      <Button variant="outline" data-testid="quote-readiness-refresh" disabled={resource.busy} onClick={()=>{if(!lock.current)setRefresh(n=>n+1);}}><RefreshCw size={14}/>{t.refresh}</Button>
      <Button data-testid="quote-provider-check" disabled={resource.busy||!value?.provider_key_configured} onClick={check}><RefreshCw size={14}/>{t.check}</Button>
      <Button data-testid="quote-request" disabled><LockKeyhole size={14}/>{t.request}</Button>
    </div>
    <div className="quote-readiness-links"><Link to="/admin" data-testid="quote-budget-link">{t.budgetLink}</Link>
      <a href="https://docs.biconomy.io/contracts-and-audits/supported-chains" target="_blank" rel="noopener noreferrer" data-testid="quote-provider-docs">{t.docs}</a></div>
    <p className="pilot-muted" data-testid="quote-readiness-boundary">{t.boundary}</p>
    <QuoteResearch data={data}/>
  </section>;
};